import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Container,
  Button,
  CircularProgress,
  Alert,
  Modal,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import { httpRequest } from "../../../../ui-utils/api";
import { apiUrl, backendHosts } from "../../../../ui-utils/apiConfig";
import { Grid } from "@material-ui/core";

const WealthVault = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [wealthVaultUserData, setWealthVaultUserData] = useState([]);
  const [portfolioData, setPortfolioData] = useState([]);
  const [openUserModal, setOpenUserModal] = useState(false);
  const [selectedSubscriptionType, setSelectedSubscriptionType] =
    useState("All");

  useEffect(() => {
    const getAllPortfolio = async () => {
      try {
        const res = await httpRequest({
          endPoint: `${backendHosts.WEALTH_VAULT}${apiUrl.getWealthVaultPortfolios}`,
          method: "get",
          instance: "instanceOne",
        });
        console.log("Portfolio data:", res.data.data);
        setPortfolioData(res.data.data || []);
      } catch (e) {
        console.log("Error fetching portfolio data:", e);
      }
    };

    const fetchUsers = async () => {
      try {
        setLoading(true);
        const res = await httpRequest({
          endPoint: `${backendHosts.WEALTH_VAULT}${apiUrl.getWealthVaultUsers}`,
          method: "post",
          instance: "instanceOne",
        });

        console.log("Users in wealth vault", res.data);
        setWealthVaultUserData(res.data || []);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching users:", err);
        setError("Failed to load users list.");
        setLoading(false);
      }
    };

    getAllPortfolio();
    fetchUsers();
  }, []);

  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const handleOpenUserModal = () => setOpenUserModal(true);
  const handleCloseUserModal = () => setOpenUserModal(false);

  const handleSubscriptionTypeChange = (event) => {
    setSelectedSubscriptionType(event.target.value);
  };

  const filteredPortfolioData = portfolioData.filter((portfolio) => {
    if (selectedSubscriptionType === "All") {
      return true;
    }
    return portfolio.subscription_type === selectedSubscriptionType;
  });

  return (
    <Container maxWidth="lg" sx={{ mt: 8, background: "white", p: 1 }}>
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Button
        variant="contained"
        color="primary"
        sx={{ mb: 2, mr: 2 }}
        onClick={handleOpenUserModal}
      >
        Check Wealth Vault Users
      </Button>

      {/* User Information Modal */}
      <Modal
        open={openUserModal}
        onClose={handleCloseUserModal}
        aria-labelledby="user-modal-title"
        aria-describedby="user-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            border: "2px solid #000",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            maxHeight: "90vh",
            overflowY: "auto",
          }}
        >
          <Typography variant="h6" component="h2" id="user-modal-title">
            User Information
          </Typography>
          <TableContainer sx={{ overflowY: "auto" }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Client ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email ID</TableCell>
                  <TableCell>Mobile No.</TableCell>
                  <TableCell>Subscription Expiry Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {wealthVaultUserData.map(
                  (
                    userData,
                    index // asc and created date
                  ) => (
                    <TableRow key={index}>
                      <TableCell>{userData.clientid}</TableCell>
                      <TableCell>{userData.name}</TableCell>
                      <TableCell>{userData.emailid}</TableCell>
                      <TableCell>{userData.mobileno}</TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            backgroundColor: (theme) =>
                              new Date(userData.sub_expire_date) < new Date()
                                ? theme.palette.error.light
                                : theme.palette.success.light,
                            p: 1,
                            borderRadius: 1,
                          }}
                        >
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {formatDate(userData.sub_expire_date)}
                          </Typography>
                          {new Date(userData.sub_expire_date) < new Date() && (
                            <Chip
                              label="Expired"
                              size="small"
                              color="error"
                              sx={{ ml: 1, fontWeight: 600 }}
                            />
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Button
            variant="contained"
            color="primary"
            sx={{ mt: 2 }}
            onClick={handleCloseUserModal}
          >
            Close
          </Button>
        </Box>
      </Modal>

      {/* Portfolio Data Section */}
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box
            sx={{
              bgcolor: "background.paper",
              p: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" component="h2" id="portfolio-modal-title">
              Portfolio Data
            </Typography>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel id="subscription-type-label">
                Subscription Type
              </InputLabel>
              <Select
                labelId="subscription-type-label"
                id="subscription-type-select"
                value={selectedSubscriptionType}
                onChange={handleSubscriptionTypeChange}
                label="Subscription Type"
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="Energetic">Energetic</MenuItem>
                <MenuItem value="Traditional">Traditional</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <TableContainer sx={{ maxHeight: 400, overflowY: "auto" }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Client ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email ID</TableCell>
                  <TableCell>Mobile No.</TableCell>
                  <TableCell>Amount</TableCell>

                  <TableCell>Created Date</TableCell>
                  <TableCell>Subscription Type</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPortfolioData.map((portfolio, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      bgcolor:
                        portfolio.subscription_type === "Energetic"
                          ? "#e0f7fa"
                          : "#fff",
                    }}
                  >
                    <TableCell>{portfolio.client_id}</TableCell>
                    <TableCell>{portfolio.name}</TableCell>
                    <TableCell>{portfolio.email}</TableCell>
                    <TableCell>{portfolio.mobile_no}</TableCell>
                    <TableCell>{portfolio.amount}</TableCell>

                    <TableCell>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          p: 1,
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {formatDate(portfolio.createdAt)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{portfolio.subscription_type}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    </Container>
  );
};

export default WealthVault;
